import { css } from "@emotion/css";
import type { PageAction } from "@octopusdeploy/design-system-components";
import { ActionButton, ActionButtonType, Button, Dialog, useShowSnackbar } from "@octopusdeploy/design-system-components";
import { CodeCommitIcon } from "@octopusdeploy/design-system-icons";
import { FilterSearchBox } from "@octopusdeploy/design-system-octopus-components";
import { borderWidth, space, themeTokens } from "@octopusdeploy/design-system-tokens";
import { useAggregateAPIOperationStatus, useMutation, useQuery } from "@octopusdeploy/octopus-react-client";
import type { ActionTemplateParameterResource, ModifyProjectTemplateParameterSetCommand, PaginatedCollection, PlatformHubAccountResource, ProcessTemplateParameterResource, ProjectTemplateParameterSetResource, } from "@octopusdeploy/octopus-server-client";
import { Repository, isProcessTemplateParameterResource, Permission } from "@octopusdeploy/octopus-server-client";
import { links } from "@octopusdeploy/portal-routes";
import { isEqual } from "lodash";
import * as React from "react";
import { useCallback, useState } from "react";
import { AddTemplateParameterButton, OpenReorderParametersButton, TemplateParametersOnboarding } from "~/areas/platformHub/TemplateParameters";
import { buildProducerProjectTemplateContextProps, ProducerProjectTemplateContext, type ProducerProjectTemplateContextProps } from "~/areas/projects/components/Process/ProjectTemplates/ProducerProjectTemplateContext";
import { useLegacyDoBusyTask } from "~/components/DataBaseComponent/useLegacyDoBusyTask";
import { useDialogTrigger } from "~/components/Dialog/DialogTrigger";
import { LegacyForm } from "~/components/FormPaperLayout/LegacyForm";
import { useSpaceAwareNavigation } from "~/components/Navigation/SpaceAwareNavigation/useSpaceAwareNavigation";
import { PageContent } from "~/components/PageContent/PageContent";
import { PermissionCheck } from "~/components/PermissionCheck";
import { hasPermission } from "~/components/PermissionCheck/PermissionCheck";
import type { ProjectTemplateContextProps } from "~/routing/pageRegistrations/ProjectTemplatePageRegistration";
import { lastAccessedPlatformHubGitRef } from "../LastAccessedPlatformHubGitRef";
import { PlatformHubPageHeaderBranchSelector } from "../PlatformHubPageHeaderBranchSelector";
import { StopShareTemplateDialog } from "../StopShareTemplateDialog";
import createProjectTemplateOverflowMenuItems, { PlatformHubTemplateType } from "../platformHubHelpers";
import { ActionCompleted } from "../processTemplates/ProcessTemplateWizardDialog";
import { createSnackbarMessage, TemplateParametersTable } from "../processTemplates/TemplateParametersTable";
import { ProjectTemplateCommitDialog } from "./ProjectTemplateCommitDialog";
import { GetProjectTemplateInitialSuccessCallout } from "./ProjectTemplateLayout";
import ProjectTemplatePublishButton from "./ProjectTemplatePublishButton";
import { ProjectTemplateWizardDialog, TemplateWizardSteps } from "./ProjectTemplateWizardDialog";
import { useProjectTemplateWizard, useIsProjectTemplateShared } from "./useProjectTemplateWizard";
import { useSaveProjectTemplate } from "./useSaveProjectTemplate";
import { useStopShareProjectTemplate } from "./useStopShareProjectTemplate";
interface ProjectTemplateBranchParametersPageProps {
    loaderData: LoaderData;
    slug: string;
    gitRef: string;
    projectTemplateContext: ProjectTemplateContextProps;
}
type LoaderData = {
    parameterSet: ProjectTemplateParameterSetResource;
    platformHubAccounts: PaginatedCollection<PlatformHubAccountResource>;
};
export async function projectTemplateBranchParametersPageLoader(repository: Repository, { routeParams }: {
    routeParams: {
        slug: string;
        gitRef: string;
    };
}): Promise<LoaderData> {
    const parameterSet = await repository.ProjectTemplates.tryGetParameterSet(routeParams.slug, routeParams.gitRef);
    const platformHubAccounts = await repository.PlatformHubAccounts.get({ Take: Repository.takeAll });
    return {
        parameterSet,
        platformHubAccounts,
    };
}
export function ProjectTemplateBranchParametersPage({ loaderData, slug, gitRef, projectTemplateContext }: ProjectTemplateBranchParametersPageProps) {
    const navigation = useSpaceAwareNavigation();
    const { projectTemplate, persistenceSettings, projectTemplateLink, publishedVersions, refetchPublishedVersions } = projectTemplateContext;
    const { status: doBusyTaskStatus } = useLegacyDoBusyTask();
    const { isInProgress } = useAggregateAPIOperationStatus(doBusyTaskStatus);
    const showSnackbar = useShowSnackbar();
    const [showInitialSuccessCallout, setShowInitialSuccessCallout] = useState(false);
    const { isOpen, openWizard, dialogProps } = useProjectTemplateWizard();
    const { isShared: isTemplateShared, refetchIsShared: refetchIsTemplateShared } = useIsProjectTemplateShared(slug);
    const { openDialog: openStopShareDialog, isOpen: isStopShareDialogOpen, closeDialog: closeStopShareDialog } = useDialogTrigger();
    const { handleStopShare } = useStopShareProjectTemplate(projectTemplate, refetchIsTemplateShared);
    const { executeSave } = useSaveProjectTemplate({
        projectTemplate,
        gitRef,
        commitHandler: dialogProps.commitHandler,
        onClose: dialogProps.onClose,
        refetchPublishedVersions,
        refetchIsTemplateShared,
        setShowInitialSuccessCallout,
    });
    const navigateToGitRef = useCallback((newGitRef: string) => {
        const routeParams = { slug, gitRef: newGitRef };
        navigation.redirect(links.projectTemplateBranchParametersPage.generateUrl(routeParams, undefined));
        lastAccessedPlatformHubGitRef.save(newGitRef, persistenceSettings);
    }, [navigation, slug, persistenceSettings]);
    const setGitRef = navigateToGitRef;
    const [producerProjectTemplateContextProps, setProducerProjectTemplateContextProps] = useState<ProducerProjectTemplateContextProps>(buildProducerProjectTemplateContextProps(projectTemplate, loaderData.parameterSet));
    const [model, setModel] = React.useState<ProjectTemplateParameterSetResource>(loaderData.parameterSet);
    const { result: cleanModel, refetch: refetchParameterSet } = useQuery(async (repository) => {
        const result = await repository.ProjectTemplates.tryGetParameterSet(slug, gitRef);
        setModel(result);
        return result;
    }, [slug, gitRef], "Get project template parameter set", {
        initialResult: loaderData.parameterSet,
    });
    const [nameFilter, setNameFilter] = React.useState<string>("");
    const { openDialog: openCommitPublishShareDialog, isOpen: isCommitPublishShareDialogOpen, closeAllDialogs: closeCommitPublishShareDialog } = useDialogTrigger();
    const onModelChange = (updatedModel: ProjectTemplateParameterSetResource) => {
        setModel(updatedModel);
        setProducerProjectTemplateContextProps(buildProducerProjectTemplateContextProps(projectTemplate, updatedModel));
    };
    const onParametersChange = (parameters: ActionTemplateParameterResource[]) => {
        const updatedModel: ProjectTemplateParameterSetResource = { ...model, Parameters: parameters.map((p) => (isProcessTemplateParameterResource(p) ? p : { ...p, Values: [] })) };
        onModelChange(updatedModel);
    };
    const onAddParameter = (parameter: ProcessTemplateParameterResource) => {
        onParametersChange([...model.Parameters, parameter]);
    };
    const { execute: saveProjectTemplateParametersSet } = useMutation({
        name: "Save project template parameters set",
        action: async (repository: Repository, command: ModifyProjectTemplateParameterSetCommand) => {
            await repository.ProjectTemplates.modifyParameterSet(command);
            return [ActionCompleted.Committed];
        },
        afterComplete: async (repository, result) => {
            refetchParameterSet();
            const snackbarMessage = createSnackbarMessage(result, "project template");
            if (snackbarMessage) {
                showSnackbar(snackbarMessage);
            }
        },
    });
    const createCommitHandler = useCallback(() => async (commitMessage: string) => {
        const command: ModifyProjectTemplateParameterSetCommand = {
            ProjectTemplateSlug: slug,
            GitRef: gitRef,
            ParameterSet: model,
            ChangeDescription: commitMessage,
        };
        await saveProjectTemplateParametersSet(command);
    }, [slug, gitRef, model, saveProjectTemplateParametersSet]);
    const getCommandToCommit = (): ModifyProjectTemplateParameterSetCommand => {
        return {
            ProjectTemplateSlug: slug,
            GitRef: gitRef,
            ParameterSet: model,
        };
    };
    const onSaveClick = async (isNavigationConfirmation: boolean) => {
        if (isNavigationConfirmation) {
            openCommitPublishShareDialog();
        }
    };
    const publishButton: PageAction = {
        type: "custom",
        content: (<PermissionCheck permission={Permission.PlatformHubEdit}>
                <ProjectTemplatePublishButton onPublish={() => openWizard([TemplateWizardSteps.Publish])}/>
            </PermissionCheck>),
        key: "Publish",
    };
    const commitButton: PageAction = {
        key: "CommitButton",
        type: "custom",
        content: (<PermissionCheck permission={Permission.PlatformHubEdit}>
                <div role="group" className={styles.commitButtonContainerStyles}>
                    <ActionButton label={"Commit"} type={ActionButtonType.Primary} accessibleName={"Commit"} onClick={() => {
                if (isTemplateShared) {
                    openWizard([TemplateWizardSteps.Commit, TemplateWizardSteps.Publish], undefined, createCommitHandler());
                }
                else {
                    openWizard([TemplateWizardSteps.Commit, TemplateWizardSteps.Publish, TemplateWizardSteps.Share], undefined, createCommitHandler());
                }
            }} disabled={isEqual(model, cleanModel) || isInProgress || !hasPermission(Permission.PlatformHubEdit)}/>
                    <Button importance="primary" icon={<CodeCommitIcon size={20}/>} accessibleName={"Quick commit"} disabled={isEqual(model, cleanModel) || isInProgress || !hasPermission(Permission.PlatformHubEdit)} onClick={() => saveProjectTemplateParametersSet({ ...getCommandToCommit(), ChangeDescription: "Update project template parameters" })}/>
                </div>
            </PermissionCheck>),
    };
    const overflowActions = createProjectTemplateOverflowMenuItems({ projectTemplateLink, onShare: () => openWizard([TemplateWizardSteps.Share]), isShared: isTemplateShared, onStopShare: openStopShareDialog });
    const callout = GetProjectTemplateInitialSuccessCallout({ showInitialSuccessCallout, setShowInitialSuccessCallout, projectTemplateName: projectTemplate.Name });
    return (<ProducerProjectTemplateContext.Provider value={producerProjectTemplateContextProps}>
            <LegacyForm devToolsDirtyTrackingKey="Project Template Parameters" disableDirtyFormChecking={false} onSaveClick={onSaveClick} savePermission={{ permission: Permission.PlatformHubEdit }} model={model} cleanModel={cleanModel} forceDisableFormSaveButton={isInProgress}>
                {() => (<PageContent header={{
                title: "Parameters",
                contextSelector: <PlatformHubPageHeaderBranchSelector setGitRef={setGitRef} gitRef={gitRef} persistenceSettings={persistenceSettings}/>,
                pageActions: [publishButton, commitButton],
                overflowActions,
            }} busy={isInProgress} callout={callout}>
                        {model.Parameters.length === 0 ? (<TemplateParametersOnboarding onAdd={onAddParameter} parameters={model.Parameters}/>) : (<>
                                <div className={styles.headerContainer}>
                                    <FilterSearchBox placeholder="Filter by..." value={nameFilter} onChange={setNameFilter}/>
                                    <div className={styles.parameterButtonsContainer}>
                                        <OpenReorderParametersButton parameters={model.Parameters} onChange={onParametersChange}/>
                                        <AddTemplateParameterButton parameters={model.Parameters} onAdd={onAddParameter} type={ActionButtonType.Secondary}/>
                                    </div>
                                </div>
                                <TemplateParametersTable parameters={model.Parameters} onChange={onParametersChange} platformHubAccounts={loaderData.platformHubAccounts.Items} templateLink={projectTemplateLink?.Link} nameFilter={nameFilter}/>
                            </>)}
                    </PageContent>)}
            </LegacyForm>

            <ProjectTemplateCommitDialog open={isCommitPublishShareDialogOpen} gitRef={gitRef} onClose={closeCommitPublishShareDialog} onCommit={saveProjectTemplateParametersSet} getCommandToCommit={getCommandToCommit} defaultCommitMessage="Update parameter set" entityName="parameter set"/>

            {isOpen && (<ProjectTemplateWizardDialog projectTemplate={projectTemplate} gitRef={gitRef} defaultBranch={persistenceSettings.DefaultBranch} stepsToShow={dialogProps.stepsToShow} getDefaultCommitMessage={() => "Update project template"} onClose={dialogProps.onClose} onInitialSuccessCallout={() => setShowInitialSuccessCallout(true)} publishedVersions={publishedVersions} executeSave={executeSave}/>)}

            <Dialog open={isStopShareDialogOpen}>
                <StopShareTemplateDialog onSave={handleStopShare} onClose={closeStopShareDialog} templateType={PlatformHubTemplateType.ProjectTemplate}/>
            </Dialog>
        </ProducerProjectTemplateContext.Provider>);
}
const styles = {
    headerContainer: css({
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        paddingLeft: space[16],
        paddingRight: space[16],
        paddingTop: space[24],
        paddingBottom: space[24],
        borderTop: `${borderWidth[1]} solid ${themeTokens.color.border.primary}`,
    }),
    parameterButtonsContainer: css({
        alignItems: "center",
        display: "flex",
        gap: space[12],
    }),
    commitButtonContainerStyles: css({
        display: "inline-flex",
        gap: space[4],
    }),
};
